/**
 * Phone lobby view - mobile-optimized lobby screen.
 *
 * Shows:
 * - Current player's name
 * - Session code (formatted ABC-123-XYZ)
 * - Player list with PlayerCard components
 * - Leave button ("Opuść grę")
 * - Real-time player updates via WebSocket
 *
 * If logged in as the session's host account: game mode selector + StartButton
 * Otherwise: shows "Czekaj na hosta..." message
 *
 * Redirects to /join/:code if no credentials in playerStore
 */

import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlayerGrid } from './PlayerGrid'
import { StartButton } from '@/components/medium/StartButton'
import { Button } from '@/components/ui/button'
import { usePlayerStore, Player } from '@/stores/playerStore'
import { useUIStore } from '@/stores/uiStore'
import { usePlayerEvents } from '@/hooks/usePlayerEvents'
import { useGameLifecycle } from '@/hooks/useGameLifecycle'
import { useSocket } from '@/hooks/useSocket'
import { useSessionGuard } from '@/hooks/useSessionGuard'
import {
  leaveSession,
  getSession,
  startGame,
  getSessionPlayers,
  getGameModeConfig,
  getGameModes,
  changeGameMode,
  getMe,
  GameConfig,
  GameModeListItem,
  ApiError,
  InsufficientCoinsDetail,
} from '@/services/api'
import type { Locale } from '@/types/auth'
import { useAuthStore } from '@/stores/authStore'
import { GameModeSelector } from '@/components/medium/GameModeSelector'
import { useGameStore } from '@/stores/gameStore'
import { useWalletStore } from '@/stores/walletStore'
import { PaywallModal } from '@/components/high/billing/PaywallModal'
import { PixelIcon } from '../../../components/low/PixelIcon'

export function PhoneLobby() {
  const { t } = useTranslation()
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { on, emit, isConnected } = useSocket()

  useSessionGuard()

   // Player store
   const playerId = usePlayerStore((s) => s.playerId)
   const playerName = usePlayerStore((s) => s.playerName)
   const sessionCode = usePlayerStore((s) => s.sessionCode)
   const sessionUuid = usePlayerStore((s) => s.sessionUuid)
   const storedGameMode = usePlayerStore((s) => s.gameMode)
   const isInitialized = usePlayerStore((s) => s.isInitialized)
   const clearPlayer = usePlayerStore((s) => s.clear)
   const isWaiting = usePlayerStore((s) => s.isWaiting)
   const setIsWaiting = usePlayerStore((s) => s.setIsWaiting)

   const sessionId = sessionUuid || sessionCode

   const [isLoading, setIsLoading] = useState(false)
   const [players, setPlayers] = useState<Player[]>([])
   const addToast = useUIStore((s) => s.addToast)
   const setPhase = useGameStore((s) => s.applyDelta)
   const [gameConfig, setGameConfig] = useState<GameConfig | null>(null)

   useGameLifecycle()
   const [gameMode, setGameMode] = useState<string>('bingo')
   const [sessionIdFromApi, setSessionIdFromApi] = useState<string | null>(null)
   const [isLeaving, setIsLeaving] = useState(false)
   const [paywallDetail, setPaywallDetail] = useState<InsufficientCoinsDetail | null>(null)
   const [pingingPlayerIds, setPingingPlayerIds] = useState<string[]>([])
   // Caller is logged in as the session's host account (computed by backend)
   const [canControl, setCanControl] = useState(false)
   const isLoggedIn = useAuthStore((s) => s.user !== null)
   const [sessionLocale, setSessionLocale] = useState<Locale | null>(null)
   const [gameModes, setGameModes] = useState<GameModeListItem[]>([])

  const handlePing = () => {
    if (!playerId || !sessionId) return
    emit('player:ping', { playerId, sessionId })
  }

  // Stable callbacks for player events
  const playerEventCallbacks = useMemo(() => ({
    onPlayerJoined: (player: Player) => {
      setPlayers((prev) => {
        if (prev.some((p) => p.id === player.id)) return prev
        return [...prev, player]
      })
    },
    onPlayerLeft: (playerId: string) => {
      setPlayers((prev) => prev.filter((p) => p.id !== playerId))
    },
    onPlayerDisconnected: (playerId: string) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, connection_status: 'disconnected' as const } : p
        )
      )
    },
    onPlayerReconnected: (playerId: string) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, connection_status: 'connected' as const } : p
        )
      )
    },
    onPlayerAway: (playerId: string) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, connection_status: 'away' as const } : p
        )
      )
    },
    onPlayerReturned: (playerId: string) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, connection_status: 'connected' as const } : p
        )
      )
    },
    onPlayerReady: (playerId: string, isReady: boolean) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, isReady } : p
        )
      )
    },
  }), [])

  usePlayerEvents(playerEventCallbacks)

  // Handle player:ping event
  useEffect(() => {
    const timeouts = new Map<string, NodeJS.Timeout>()

    const unsubscribe = on<{ playerId: string }>('player:ping', (data) => {
      // Clear existing timeout for this player (reset animation)
      const existingTimeout = timeouts.get(data.playerId)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }

      // Add/refresh player in pinging list
      setPingingPlayerIds((prev) => {
        if (prev.includes(data.playerId)) {
          return prev // Already pinging, just reset timer
        }
        return [...prev, data.playerId]
      })

      // Set new timeout to remove ping after 1s
      const timeout = setTimeout(() => {
        setPingingPlayerIds((prev) => prev.filter((id) => id !== data.playerId))
        timeouts.delete(data.playerId)
      }, 1000)

      timeouts.set(data.playerId, timeout)
    })

    return () => {
      unsubscribe()
      // Clear all timeouts on unmount
      timeouts.forEach((timeout) => clearTimeout(timeout))
    }
  }, [on])

  // WS listener: game mode changed
  useEffect(() => {
    const unsubscribe = on<{ game_mode: string; min_players: number; max_players: number }>(
      'session:game_mode_changed',
      (data) => {
        setGameMode(data.game_mode)
        setGameConfig({ min_players: data.min_players, max_players: data.max_players })
      }
    )
    return unsubscribe
  }, [on])

  useEffect(() => {
    const unsubscribe = on<{ game_mode: string; session_id: string }>(
      'game:started',
      (data) => {
        setPhase({ phase: 'playing' })
        const targetSessionId = data.session_id || sessionIdFromApi || sessionId
        if (!targetSessionId) return
        navigate(`/game/${targetSessionId}`, { replace: true })
      }
    )
    return unsubscribe
  }, [on, navigate, setPhase, sessionId, sessionIdFromApi])

  // AC4: When game ends, transition from Poczekalnia to normal lobby
  useEffect(() => {
    const unsubscribe = on('game:ended', () => {
      if (isWaiting) {
        setIsWaiting(false)
      }
    })
    return unsubscribe
  }, [on, isWaiting, setIsWaiting])

  const resolveStatus = (value: unknown): Player['connection_status'] => {
    if (value === 'connecting' || value === 'connected' || value === 'disconnected' || value === 'away') {
      return value
    }
    return 'connected'
  }

  useEffect(() => {
    const unsubscribe = on<{
      players: Array<{
        id: string
        name: string
        is_host: boolean
        is_ready: boolean
        role: 'phone' | 'tv'
        connection_status: string
      }>
    }>('session:player_list', (data) => {
      setPlayers(
        data.players.map((p) => ({
          id: p.id,
          name: p.name,
          is_host: p.is_host,
          is_ready: p.is_ready,
          role: p.role || 'phone',
          connection_status: resolveStatus(p.connection_status),
        }))
      )
    })
    return unsubscribe
  }, [on])

  useEffect(() => {
    async function fetchData() {
      if (!code) return
       try {
          const sessionData = await getSession(code)
          setSessionIdFromApi(sessionData.id)
          setSessionLocale(sessionData.locale)
          setGameMode(sessionData.game_mode ?? storedGameMode ?? 'bingo')
          if (sessionData.status === 'playing') {
            navigate(`/game/${sessionData.id}`, { replace: true })
            return
          }
          if (sessionData.game_mode) {
            if (sessionData.game_config) setGameConfig(sessionData.game_config)
          } else if (storedGameMode) {
            try {
              const config = await getGameModeConfig(storedGameMode)
              setGameConfig(config)
            } catch {
              if (sessionData.game_config) setGameConfig(sessionData.game_config)
            }
          } else if (sessionData.game_config) {
            setGameConfig(sessionData.game_config)
          }
          const data = await getSessionPlayers(code)
          setPlayers(
            data.players.map((p: Record<string, unknown>) => ({
              id: String(p.id),
              name: String(p.name),
              is_host: Boolean(p.is_host),
              is_ready: Boolean(p.is_ready),
              role: (p.role as 'phone' | 'tv') || 'phone',
              connection_status: resolveStatus(p.connection_status),
            }))
          )
       } catch (error) {
         const apiError = error as ApiError
         addToast({
           type: 'error',
           message: apiError.message ?? t('lobby.loadDataError'),
           duration: 5000,
         })
       } finally {
         setIsLoading(false)
       }
    }
      fetchData()
    }, [code, addToast, storedGameMode, navigate, t])

    // Socket.IO room subscription happens asynchronously after mount. If a
    // player joins the session (REST API) in the gap between the initial
    // REST fetch above and the socket actually joining the room, the
    // player:joined / session:player_list broadcasts are missed entirely
    // (Socket.IO does not queue events for late subscribers). Once the
    // socket reports connected, re-fetch the player list via REST as a
    // deterministic catch-up — this backfills any joins missed during the
    // connection race without relying on waitForTimeout or raised timeouts.
    useEffect(() => {
      if (!isConnected || !code) return
      getSessionPlayers(code)
        .then((data) => {
          setPlayers((prev) => {
            const existingIds = new Set(prev.map((p) => p.id))
            const missing = data.players
              .filter((p: Record<string, unknown>) => !existingIds.has(String(p.id)))
              .map((p: Record<string, unknown>) => ({
                id: String(p.id),
                name: String(p.name),
                is_host: Boolean(p.is_host),
                is_ready: Boolean(p.is_ready),
                role: (p.role as 'phone' | 'tv') || 'phone',
                connection_status: resolveStatus(p.connection_status),
              }))
            return missing.length > 0 ? [...prev, ...missing] : prev
          })
        })
        .catch(() => {
          // Best-effort catch-up — WS events remain the primary path.
        })
    }, [isConnected, code])

    // Sole owner of canControl, separate from fetchData so the extra login
    // refresh does not delay the initial player list. The access cookie lives
    // only as long as the token (15 min) and then disappears, so GET /sessions
    // would see a guest; getMe's 401 makes authFetch refresh the login first.
    useEffect(() => {
      if (!code || !isLoggedIn) return
      let cancelled = false
      getMe()
        .catch(() => undefined)
        .then(() => getSession(code))
        .then((sessionData) => {
          if (!cancelled) setCanControl(sessionData.can_control)
        })
        .catch((error) => console.error('[PhoneLobby] Failed to resolve lobby control:', error))
      return () => {
        cancelled = true
      }
    }, [code, isLoggedIn])

    // Mode catalog only for the host account, in the session's locale (as LobbyScreen)
    useEffect(() => {
      if (!canControl || sessionLocale === null) return
      const resolvedSessionLocale: Locale = sessionLocale
      getGameModes(resolvedSessionLocale)
        .then((data) => setGameModes(data.modes))
        .catch((error) => console.error('[PhoneLobby] Failed to fetch game modes:', error))
    }, [canControl, sessionLocale])

    useEffect(() => {
      if (!isInitialized) return
      if (playerId && playerName) return
      navigate(`/join/${code}`, { replace: true })
    }, [isInitialized, playerId, playerName, code, navigate])

  const handleLeave = async () => {
    if (!playerId) return
    setIsLeaving(true)
    try {
      await leaveSession(playerId)
      clearPlayer()
      navigate('/', { replace: true })
    } catch (error) {
      const apiError = error as ApiError
      addToast({
        type: 'error',
        message: apiError.message ?? t('lobby.leaveGameError'),
        duration: 5000,
      })
      setIsLeaving(false)
    }
  }

  // State is updated by the session:game_mode_changed listener
  const handleGameModeChange = async (newMode: string) => {
    if (!code || newMode === gameMode) return
    try {
      await changeGameMode(code, newMode)
    } catch (error) {
      const apiError = error as ApiError
      addToast({
        type: 'error',
        message: apiError.message ?? t('lobby.changeModeError'),
        duration: 5000,
      })
    }
  }

  const handleStartGame = async () => {
    if (!code) return
    try {
      const result = await startGame(code, gameMode)
      // The start just cost the host coins; refresh so the header stops showing
      // the pre-charge balance.
      void useWalletStore.getState().refresh()
      setPhase({ phase: result.status as 'lobby' | 'playing' | 'game_over' })
      const targetSessionId = result.id ?? sessionId ?? sessionIdFromApi
      if (!targetSessionId) return
      navigate(`/game/${targetSessionId}/phone`, { replace: true })
    } catch (error) {
      const apiError = error as ApiError
      if (apiError.insufficientCoinsDetail) {
        // An empty wallet is not an error to dismiss — it is an offer to buy.
        setPaywallDetail(apiError.insufficientCoinsDetail)
        return
      }
      addToast({
        type: 'error',
        message: apiError.message ?? t('lobby.startGameError'),
        duration: 5000,
      })
    }
  }

   const totalCount = players.filter((p) => p.role === 'phone').length
   const minPlayers = gameConfig?.min_players ?? 2
   const maxPlayers = gameConfig?.max_players ?? 15
   const canStart = totalCount >= minPlayers && canControl

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  // AC3: Poczekalnia — late join during PLAYING
  if (isWaiting) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-8 px-6">
        <div className="text-center space-y-4">
          <div className="text-6xl animate-pulse">⏳</div>
          <h1 className="text-2xl font-display font-bold text-primary uppercase tracking-[0.2em]">
            {t('lobby.title')}
          </h1>
          <p className="text-muted-foreground text-sm uppercase tracking-widest">
            {t("lobby.gameInProgressRejoin")}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest">
          <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
          <span>{t('lobby.sessionColon')} <span className="text-foreground font-bold">{code}</span></span>
        </div>
        <button
          onClick={handleLeave}
          disabled={isLeaving}
          className="text-destructive text-xs uppercase tracking-widest hover:underline disabled:opacity-50"
        >
          {isLeaving ? t("lobby.leaving") : t("lobby.leaveGame")}
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col selection:bg-primary/30 overflow-hidden relative">
      {/* Header */}
      <header className="flex-shrink-0 bg-card/80 backdrop-blur-md border-b border-border px-6 py-4 flex justify-between items-center relative z-10">
        <button
          onClick={() => navigate('/')}
          aria-label={t('nav.homeAriaLabel')}
          className="p-2 text-muted-foreground hover:text-foreground transition-colors text-2xl min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <PixelIcon>🏠</PixelIcon>
        </button>
        <div className="text-center">
          <h1 className="text-lg font-display font-bold tracking-[0.3em] text-primary uppercase">{t('lobby.title')}</h1>
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{t('lobby.waitingForPlayers')}</p>
        </div>
        <button
          onClick={handleLeave}
          disabled={isLeaving}
          aria-label={t('lobby.leaveGame')}
          className="p-2 text-destructive hover:bg-destructive/10 rounded-none transition-none disabled:opacity-50 text-2xl min-h-[44px] min-w-[44px] flex items-center justify-center"
        >
          <PixelIcon>{isLeaving ? '⏳' : '👋'}</PixelIcon>
        </button>
      </header>

      {/* Session Info Bar */}
      <div className="bg-card border-b border-border px-6 py-3 flex justify-between items-center text-[10px] uppercase tracking-widest text-muted-foreground relative z-10">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
          <span>{t('lobby.sessionColon')} <span className="text-foreground font-bold">{code}</span></span>
        </div>
        <div className="flex items-center gap-4">
          <span>{t('common.mode')} <span className="text-foreground font-bold">{gameMode}</span></span>
          <span>{t('lobby.playersColon')} <span className="text-foreground font-bold" data-testid="lobby-player-count">
            {totalCount}/{gameConfig === null
              ? <span className="inline-block bg-muted animate-pulse rounded w-6 h-4 align-middle" />
              : maxPlayers}
          </span></span>
        </div>
      </div>

      {/* Player List */}
      <main className="flex-1 overflow-y-auto p-6 relative z-10 custom-scrollbar">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t('lobby.connectedUnits')}</h2>
          <div className="h-[1px] flex-grow ml-4 bg-border" />
        </div>
        <PlayerGrid
          players={players}
          currentPlayerId={playerId || undefined}
          pingingPlayerIds={pingingPlayerIds}
        />
      </main>

      {/* Footer Actions */}
      <footer className="flex-shrink-0 p-6 bg-card/80 backdrop-blur-xl border-t border-border relative z-10">
        <div className="flex flex-col gap-4">
          {/* Ping Button */}
          <Button
            onClick={handlePing}
            variant="outline"
            className="w-full border-secondary text-secondary hover:bg-secondary/20 min-h-[44px] font-black uppercase tracking-[0.3em] flex items-center justify-center gap-3"
          >
            <PixelIcon className="text-2xl">⚡</PixelIcon>
            {t('lobby.sendPing')}
          </Button>

          {canControl && gameModes.length > 0 && (
            <GameModeSelector
              gameModes={gameModes}
              selectedModeId={gameMode}
              onSelect={handleGameModeChange}
            />
          )}

          {canControl ? (
            gameConfig === null ? (
              <div className="w-full bg-muted animate-pulse rounded-xl h-14" />
            ) : (
              <StartButton
                disabled={!canStart}
                totalCount={totalCount}
                minPlayers={minPlayers}
                onClick={handleStartGame}
              />
            )
          ) : (
            <div className="py-4 text-center border border-border bg-muted/50 rounded">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground animate-pulse">
                {t('lobby.awaitingHostAuth')}
              </p>
            </div>
          )}
        </div>
      </footer>

      {paywallDetail && (
        <PaywallModal
          open
          onClose={() => setPaywallDetail(null)}
          trigger="insufficient_coins"
          plans={paywallDetail.upgrade_options.map((option) => ({
            id: option.code,
            name: option.name,
            price_pln: option.price_pln,
            coins: option.coins,
            days: option.days,
          }))}
          message={paywallDetail.message}
        />
      )}
    </div>
  )
}
